import React from 'react';
import MarkdownToJsx from 'markdown-to-jsx';
import { segmentText, type ParsedMarker } from '@/utils/uiTags';
import styles from './Markdown.module.css';

export interface MarkdownProps {
    children: string;
    className?: string;
    /**
     * Optional renderer for [[ui:type:id:payload]] markers embedded in the
     * assistant text by the Elrond backend. When provided, markers are split
     * out of the text and rendered via this callback; unknown ids should return
     * null (the marker is silently dropped per the spec §11 defensive rules).
     * When omitted, the entire text is rendered as plain Markdown.
     */
    renderUITag?: (marker: ParsedMarker) => React.ReactNode | null;
}

const MD_OPTIONS = {
    overrides: {
        a: ({ href, children: linkChildren }: { href?: string; children?: React.ReactNode }) => (
            <a href={href} target='_blank' rel='noopener noreferrer'>{linkChildren}</a>
        ),
    },
} as const;

const Markdown: React.FC<MarkdownProps> = ({ children, className, renderUITag }) => {
    const content = renderUITag ? (
        segmentText(children).map((seg, i) => {
            if (seg.kind === 'text') {
                return seg.value
                    ? <MarkdownToJsx key={i} options={MD_OPTIONS}>{seg.value}</MarkdownToJsx>
                    : null;
            }
            // marker segment — delegate to caller; drop silently on null
            const node = renderUITag(seg.marker);
            return node != null ? <React.Fragment key={i}>{node}</React.Fragment> : null;
        })
    ) : (
        <MarkdownToJsx options={MD_OPTIONS}>{children}</MarkdownToJsx>
    );

    return (
        <div className={[styles.markdown, className].filter(Boolean).join(' ')}>
            {content}
        </div>
    );
};

export default Markdown;
